import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { getCurrentUser, signOut as apiSignOut } from '../api/user';
import type { AuthUser } from '../api/user';

interface Props {
  children: React.ReactNode;
}

/**
 * RequireAuth wraps all authenticated routes.
 *
 * On mount it calls GET /api/user to determine the auth state:
 * - Shows a full-page spinner while loading
 * - Redirects to /login if the user is unauthenticated
 * - Provides AuthContext (user + signOut) to children once authenticated
 */
export default function RequireAuth({ children }: Props) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getCurrentUser()
      .then((u) => {
        setUser(u);
      })
      .catch(() => {
        navigate('/login', { replace: true });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate]);

  const handleSignOut = useCallback(async () => {
    try {
      await apiSignOut();
    } finally {
      window.location.href = '/login';
    }
  }, []);

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status" aria-label="Loading" />
      </div>
    );
  }

  if (!user) {
    return null; // navigate('/login') already called
  }

  return (
    <AuthContext.Provider value={{ user, signOut: handleSignOut }}>
      {children}
    </AuthContext.Provider>
  );
}
